import axios from "axios"
import { config } from "Utils/config"



const imageCache = new Map()

const CACHE_TTL_SUCCESS = 5 * 60 * 1000  // 5 min
const CACHE_TTL_ERROR = 30 * 1000         // 30 sec

function getCached(key) {
  const entry = imageCache.get(key)
  if (!entry) return undefined
  if (Date.now() > entry.expiresAt) {
    imageCache.delete(key)
    return undefined
  }
  return entry.url
}

function setCached(key, url, isError = false) {
  imageCache.set(key, {
    url,
    expiresAt: Date.now() + (isError ? CACHE_TTL_ERROR : CACHE_TTL_SUCCESS),
  })
}

const EMPTY_IMAGE_BLOB_URL = (() => {
  const byteCharacters = atob("iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR4nGNgYAAAAAMAAWgmWQ0AAAAASUVORK5CYII=")
  const byteArrays = [new Uint8Array([...byteCharacters].map(c => c.charCodeAt(0)))]
  const blob = new Blob(byteArrays, { type: "image/png" })
  return URL.createObjectURL(blob)
})()

export async function getPageImageUrl(businessId, pageId, uploadNumber = "", token, type = "pageThumbnail") {
  const cacheKey = `${type}:${businessId}:${pageId}:${uploadNumber}`

  const cached = getCached(cacheKey)
  if (cached !== undefined) return cached

  const url = `${config.API_URL}/images/${type}/${businessId}/${pageId}${uploadNumber}`

  try {
    const response = await axios.get(url, {
      headers: { token },
      responseType: "blob",
    })

    // Le back legacy répond 200 avec une erreur JSON (`CANNOT_ACCESS_TO_FOLDER`…) : ce n'est pas une
    // image — la traiter comme un échec, jamais la garder en cache comme une image.
    const type = response?.data?.type || response?.headers?.["content-type"] || ""
    if (!String(type).startsWith("image/")) throw new Error(`not an image (${type || "unknown type"})`)
    const blobUrl = URL.createObjectURL(response.data)
    setCached(cacheKey, blobUrl)
    return blobUrl
  } catch (err) {
    console.warn(`[getPageImageUrl] Failed for ${url}`, err)
    setCached(cacheKey, EMPTY_IMAGE_BLOB_URL, true)
    return EMPTY_IMAGE_BLOB_URL
  }
}

/**
 * Image d'un dashboard dans le stockage GRC (lot 5) : `GET /images/dashboard/:pageId/:fileId` du back
 * GRC (`APOLLO_SERVER_URL`, comme les vignettes), jeton Bearer — le client vient du jeton. Même cache
 * et même repli que `getPageImageUrl` : un refus (403/404, corps vide) ou une réponse qui n'est pas
 * une image n'est jamais gardé comme image.
 */
export async function getDashboardImageUrl(pageId, fileId, token) {
  const cacheKey = `dashboardImage:${pageId}:${fileId}`

  const cached = getCached(cacheKey)
  if (cached !== undefined) return cached

  const url = `${config.APOLLO_SERVER_URL}/images/dashboard/${pageId}/${fileId}`

  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: "blob",
    })
    const type = response?.data?.type || response?.headers?.["content-type"] || ""
    if (!String(type).startsWith("image/")) throw new Error(`not an image (${type || "unknown type"})`)
    const blobUrl = URL.createObjectURL(response.data)
    setCached(cacheKey, blobUrl)
    return blobUrl
  } catch (err) {
    console.warn(`[getDashboardImageUrl] Failed for ${url}`, err)
    setCached(cacheKey, EMPTY_IMAGE_BLOB_URL, true)
    return EMPTY_IMAGE_BLOB_URL
  }
}

/**
 * Fond d'une page : référence v2 (`backgroundPageConfig.storage === "v2"`, `s3FileId`) lue sur le
 * back GRC ; sinon l'image legacy `pageBackground` (compteur `backgroundImageUpload`), inchangée.
 * Sans image envoyée (`backgroundImageIsUploaded`, le drapeau que lisent les réglages de la page —
 * le compteur, lui, vaut 0 dès la création) ou sans client connu : aucune requête, pas de fond (`""`).
 */
export function getPageBackgroundUrl(page, businessId, token) {
  const pageId = page?._id?.$oid
  const background = page?.backgroundPageConfig
  if (background?.storage === "v2" && background?.s3FileId) return getDashboardImageUrl(pageId, background.s3FileId, token)
  if (!pageId || !businessId || !background?.backgroundImageIsUploaded) return Promise.resolve("")
  return getPageImageUrl(businessId, pageId, background?.backgroundImageUpload, token, "pageBackground")
}

/** Image d'un widget Image (`imageToLoad`) : route v2 de la page, ou emplacement legacy. */
export function loadWidgetImage(wanted, { pageId, businessId, token }) {
  if (wanted?.storage === "v2") return getDashboardImageUrl(pageId, wanted.fileId, token)
  return getPageImageUrl(businessId, wanted.id, undefined, token, wanted.directory)
}
